import React from 'react';
import { Phone, MessageCircle, MapPin, Shield, Sparkles } from 'lucide-react';
import { mockCompanyInfo } from '../mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'About Us', href: '#about' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Contact', href: '#contact' },
    { name: 'Get Quote', href: '#quote' }
  ];

  const services = [
    'Residential Cleaning',
    'Commercial Cleaning',
    'Security Guards',
    'Alarm Systems',
    'Event Security',
    'Property Monitoring'
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">L</span>
              </div>
              <div>
                <div className="text-xl font-bold">La-Klin</div>
                <div className="text-sm text-gray-400">Security & Cleaning</div>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              {mockCompanyInfo.tagline}
            </p>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-red-400" />
                <span className="text-sm text-gray-300">Security</span>
              </div>
              <div className="flex items-center space-x-2">
                <Sparkles className="w-4 h-4 text-red-400" />
                <span className="text-sm text-gray-300">Cleaning</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-red-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-gray-300 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact Us</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-400">24/7 Emergency Line</p>
                  <a 
                    href={`tel:${mockCompanyInfo.phone}`}
                    className="text-white hover:text-red-400 transition-colors duration-200 font-medium"
                  >
                    {mockCompanyInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MessageCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-400">WhatsApp</p>
                  <a 
                    href={`https://wa.me/${mockCompanyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-red-400 transition-colors duration-200"
                  >
                    {mockCompanyInfo.whatsapp}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-400">Address</p>
                  <p className="text-white text-sm">{mockCompanyInfo.address}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div className="py-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Service Areas:</h4>
              <div className="flex flex-wrap gap-2">
                {mockCompanyInfo.provinces.map((province) => (
                  <span 
                    key={province}
                    className="bg-gray-800 px-3 py-1 rounded-full text-xs text-gray-300"
                  >
                    {province}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-400 mb-1">{mockCompanyInfo.hours}</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-400 mb-4 md:mb-0">
              © {currentYear} {mockCompanyInfo.name}. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="#" className="hover:text-red-400 transition-colors duration-200">Privacy Policy</a>
              <a href="#" className="hover:text-red-400 transition-colors duration-200">Terms of Service</a>
              <a href="#" className="hover:text-red-400 transition-colors duration-200">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};